import { simboloMoneda } from './moneda';
// src/dominio/logicaTiendaPublica.js
// Lógica pura de la tienda pública (catálogo, carrito y seguimiento).
// Sin Firestore ni Functions: todo opera sobre el catálogo ya recibido.

/** Umbral público de «Últimas unidades». El stock exacto no se muestra. */
export const UMBRAL_ULTIMAS_UNIDADES = 5;
/** Límites que también valida el backend al crear el pedido. */
export const MAX_LINEAS_PEDIDO = 10;
export const MAX_UNIDADES_LINEA = 20;

export const ORDENES_LISTADO = [
  { id: 'recomendados', etiqueta: 'Recomendados' },
  { id: 'precio-asc', etiqueta: 'Precio: menor a mayor' },
  { id: 'precio-desc', etiqueta: 'Precio: mayor a menor' },
  { id: 'nombre', etiqueta: 'Nombre: A a Z' },
];

/** Minúsculas y sin tildes: «Proteína» se encuentra con «proteina». */
export const normalizarTexto = (texto) => String(texto || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export const categoriaDe = (producto) => producto?.categoria || 'General';
export const marcaDe = (producto) => producto?.marca || '';

/** Stock vendible del producto: la suma de variantes si las tiene. */
export const stockPublico = (producto) => {
  const variantes = Array.isArray(producto?.variantes) ? producto.variantes : [];
  if (variantes.length) return variantes.reduce((s, v) => s + Math.max(0, Number(v.stock) || 0), 0);
  return Math.max(0, Number(producto?.stock) || 0);
};

/** 'agotado' | 'ultimas' | 'disponible' para la insignia pública. */
export const nivelStock = (stock) => {
  const n = Number(stock) || 0;
  if (n <= 0) return 'agotado';
  if (n <= UMBRAL_ULTIMAS_UNIDADES) return 'ultimas';
  return 'disponible';
};

export const ETIQUETA_NIVEL_STOCK = { agotado: 'Agotado', ultimas: 'Últimas unidades', disponible: 'Disponible' };

export const filtrarProductos = (productos = [], { busqueda = '', categoria = 'Todos', marca = 'Todas', soloConStock = false, favoritos = null } = {}) => {
  const consulta = normalizarTexto(busqueda);
  return productos.filter((p) => {
    if (categoria !== 'Todos' && categoriaDe(p) !== categoria) return false;
    if (marca !== 'Todas' && marcaDe(p) !== marca) return false;
    if (soloConStock && stockPublico(p) <= 0) return false;
    if (favoritos && !favoritos.has(p.id)) return false;
    if (!consulta) return true;
    return normalizarTexto(`${p.nombre} ${p.descripcion || ''} ${p.sku || ''} ${categoriaDe(p)} ${marcaDe(p)}`).includes(consulta);
  });
};

export const ordenarProductos = (productos = [], orden = 'recomendados') => {
  const lista = [...productos];
  if (orden === 'precio-asc') return lista.sort((a, b) => Number(a.precio) - Number(b.precio));
  if (orden === 'precio-desc') return lista.sort((a, b) => Number(b.precio) - Number(a.precio));
  if (orden === 'nombre') return lista.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
  // Recomendados: primero lo que tiene stock, conservando el orden publicado.
  return lista.sort((a, b) => Number(stockPublico(b) > 0) - Number(stockPublico(a) > 0));
};

/** Categorías con su conteo, en el orden en que aparecen. */
export const categoriasConConteo = (productos = []) => {
  const conteo = new Map();
  productos.forEach((p) => conteo.set(categoriaDe(p), (conteo.get(categoriaDe(p)) || 0) + 1));
  return [{ nombre: 'Todos', total: productos.length }, ...[...conteo].map(([nombre, total]) => ({ nombre, total }))];
};

/** Marcas presentes con su conteo, por nombre. Sin marcas publicadas: lista vacía (no se muestra el filtro). */
export const marcasConConteo = (productos = []) => {
  const conteo = new Map();
  productos.forEach((p) => { const m = marcaDe(p); if (m) conteo.set(m, (conteo.get(m) || 0) + 1); });
  if (!conteo.size) return [];
  return [{ nombre: 'Todas', total: productos.length }, ...[...conteo].sort((a, b) => a[0].localeCompare(b[0], 'es')).map(([nombre, total]) => ({ nombre, total }))];
};

/** Soles con separador de miles: S/ 1,150.00 (DESIGN.md). */
export const soles = (monto) => `${simboloMoneda()} ${(Number(monto) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const solesDeCentimos = (centimos) => soles((Number(centimos) || 0) / 100);

/** Clave única de una línea del carrito (producto + talla + color). */
export const claveLinea = (productoId, talla = '', color = '') => JSON.stringify([productoId, talla || '', color || '']);

/** Stock disponible para la selección concreta (variante o producto). */
export const stockDeSeleccion = (producto, { varianteId = '', talla = '', color = '' } = {}) => {
  const variantes = Array.isArray(producto?.variantes) ? producto.variantes : [];
  if (variantes.length) {
    const v = variantes.find((x) => (varianteId && x.id === varianteId) || (!varianteId && x.talla === talla && x.color === color));
    return v ? Math.max(0, Number(v.stock) || 0) : 0;
  }
  return Math.max(0, Number(producto?.stock) || 0);
};

/** Cuántas unidades más admite la línea sin pasar el stock ni el tope por línea. */
export const unidadesAgregables = ({ stock, enCarrito = 0 }) =>
  Math.max(0, Math.min(MAX_UNIDADES_LINEA, Number(stock) || 0) - (Number(enCarrito) || 0));

// ── Pedido: etiquetas y línea de tiempo ─────────────────────────────────
export const ESTADOS_PEDIDO_PUBLICO = {
  pendiente_validacion: 'Esperando confirmación del gimnasio',
  pendiente_pago: 'Esperando tu pago',
  pagada: 'Pago confirmado',
  preparando: 'Preparando tu pedido',
  lista_retiro: 'Listo para retirar',
  en_delivery: 'En reparto',
  entregada: 'Entregado',
  cancelada: 'Cancelado',
  anulada_externamente: 'Anulado',
  bloqueada: 'En revisión del gimnasio',
  pendiente: 'Pendiente',
  confirmado_manual: 'Verificado por el gimnasio',
  confirmado_pruebas: 'Aprobado con dinero ficticio',
  reembolsado_pruebas: 'Reembolso de prueba confirmado',
  confirmado_pasarela: 'Pago aprobado por Mercado Pago',
  reembolsado_pasarela: 'Reembolso confirmado por Mercado Pago',
  reembolsado_manual: 'Reembolso registrado',
  revisar_pago: 'Requiere revisión',
  revisar_reembolso: 'Reembolso en revisión',
  pago_recibido_revision: 'En revisión; contacta al gimnasio',
  devolucion_pendiente: 'Devolución en curso',
  devuelta: 'Devolución completada',
};

/** Pago confirmado (por el gym o la pasarela): el cliente puede bajar su comprobante. */
export const pedidoPagado = (estadoPago) => estadoPago === 'pagada' || /^confirmado_/.test(String(estadoPago || ''));

const METODOS_COMPROBANTE = { yape: 'Yape', plin: 'Plin', transferencia: 'Transferencia', tarjeta: 'Tarjeta', efectivo: 'Efectivo' };
const PASARELAS_COMPROBANTE = { mercadopago: 'Mercado Pago', culqi: 'Culqi', izipay: 'Izipay' };

/** Datos del PDF del comprobante interno de un pedido, desde la vista pública (sin datos personales). */
export const datosComprobantePedido = (pedido, { nombreTienda = '', logo = '' } = {}) => ({
  gym: { nombre: nombreTienda, logo },
  titulo: 'TICKET DEL PEDIDO',
  numero: pedido?.numeroPedido || pedido?.id || '',
  fecha: Number.isFinite(pedido?.creado) ? pedido.creado : null,
  cliente: null,
  metodo: pedido?.modoCobro && pedido.modoCobro !== 'manual'
    ? PASARELAS_COMPROBANTE[pedido.proveedorCobro || 'mercadopago'] || 'Pago en línea'
    : METODOS_COMPROBANTE[pedido?.pagoReportado?.metodo] || '',
  items: (pedido?.items || []).map((item) => ({
    cantidad: item.cantidad, nombre: item.nombre || 'Producto',
    variante: [item.talla && `Talla ${item.talla}`, item.color && `Color ${item.color}`].filter(Boolean).join(', '),
    subtotalCentimos: Number.isFinite(item.subtotalCentimos) ? item.subtotalCentimos : (Number(item.precioCentimos) || 0) * (Number(item.cantidad) || 0),
  })),
  totalCentimos: pedido?.totalCentimos,
  prueba: pedido?.estadoPago === 'confirmado_pruebas',
  nota: '¡Gracias por tu compra!',
});

export const etiquetaEstado = (codigo) => ESTADOS_PEDIDO_PUBLICO[codigo] || (codigo ? 'En proceso' : 'Pendiente');

/** Pasos de la línea de tiempo según la entrega elegida. */
export const pasosPedido = (entrega = 'retiro') => [
  { id: 'recibido', etiqueta: 'Pedido recibido' },
  { id: 'confirmado', etiqueta: 'Stock reservado' },
  { id: 'pagado', etiqueta: 'Pago confirmado' },
  { id: 'listo', etiqueta: entrega === 'provincia' ? 'Enviado por agencia' : entrega === 'delivery' ? 'En reparto' : 'Listo para retirar' },
  { id: 'entregado', etiqueta: 'Entregado' },
];

const INDICE_POR_ESTADO = {
  pendiente_validacion: 0, pendiente_pago: 1, pagada: 2, preparando: 2,
  lista_retiro: 3, en_delivery: 3, entregada: 4,
};

/** Índice del paso actual, o null si el pedido salió del flujo normal. */
export const indicePasoPedido = (estado) => (estado in INDICE_POR_ESTADO ? INDICE_POR_ESTADO[estado] : (estado ? null : 0));

export const pedidoTerminado = (estado) => ['entregada', 'cancelada', 'anulada_externamente', 'devuelta'].includes(estado);

/** Métodos que el comprador puede reportar con «Ya yapié». */
export const METODOS_REPORTABLES = { yape: 'Yape', plin: 'Plin', transferencia: 'Transferencia' };

// Sin lista publicada se ofrece Yape; una lista sin medios reportables (por
// ejemplo, solo tarjeta) no ofrece «Ya yapié»: el backend también lo rechaza.
export const metodosReportables = (metodosPago) => {
  if (!Array.isArray(metodosPago) || !metodosPago.length) return ['yape'];
  return metodosPago.filter((m) => METODOS_REPORTABLES[m]);
};

/**
 * Foto que se muestra: la del color elegido (ropa); sin ella, la principal; y si
 * no hay principal, la del primer color con foto.
 */
export const fotoDeProducto = (producto, color = '') => {
  const fotos = producto?.fotosColor && typeof producto.fotosColor === 'object' ? producto.fotosColor : {};
  const delColor = color && Object.prototype.hasOwnProperty.call(fotos, color) ? fotos[color] : '';
  const valida = (url) => (typeof url === 'string' && url ? url : '');
  return valida(delColor) || valida(producto?.fotoUrl) || Object.values(fotos).map(valida).find(Boolean) || '';
};

/**
 * Galería del detalle: primero la foto que corresponde al color elegido, luego
 * la principal y las adicionales (hasta 3 en total por producto), sin repetir.
 */
export const galeriaProducto = (producto, color = '') => {
  const valida = (url) => (typeof url === 'string' && url ? url : '');
  const extra = Array.isArray(producto?.fotosExtra) ? producto.fotosExtra : [];
  return [...new Set([fotoDeProducto(producto, color), producto?.fotoUrl, ...extra].map(valida).filter(Boolean))];
};

// Logos de pago que muestra la tienda: con pasarela, lo que esa pasarela cobra;
// en manual, lo que el gym publicó («tarjeta» es pago con Visa o Mastercard).
const MEDIOS_PASARELA = {
  mercadopago: ['visa', 'mastercard', 'yape'],
  culqi: ['visa', 'mastercard', 'yape'],
  izipay: ['visa', 'mastercard', 'yape', 'plin'],
};
export const mediosPagoTienda = ({ automatico, proveedor, metodosPago }) => {
  if (automatico) return MEDIOS_PASARELA[proveedor] || MEDIOS_PASARELA.mercadopago;
  const lista = Array.isArray(metodosPago) ? metodosPago : [];
  return [...new Set(lista.flatMap((m) => (m === 'tarjeta' ? ['visa', 'mastercard']
    : ['yape', 'plin', 'transferencia'].includes(m) ? [m] : [])))];
};

/** N.º de operación o código de seguridad de Yape (3 dígitos): 3 a 30 letras o
 * dígitos. Solo informa al gym, que verifica el abono; lo valida también el backend. */
export const referenciaValida = (referencia) => /^[A-Za-z0-9]{3,30}$/.test(String(referencia || '').trim());
